/**
 * Read a per-locale value out of an admin DTO's `Record<string, string>`
 * field. Mirrors the backend service-layer fallback order:
 *
 *   1. Requested locale (the active i18next language, normalised below)
 *   2. en-CA (always present after V.a's backfill)
 *   3. Any other key in the map (defensive — covers tenants that opt out
 *      of EN entirely; rare but possible after V.c's supportedLocales)
 *   4. Empty string
 *
 * Mirrors the customer/admin contract bifurcation from PR V.b:
 * **customer endpoints** return scalar locale-resolved strings (no
 * `pickLocaleValue` call needed at the call site); **admin endpoints**
 * return per-locale maps and consumers use this helper.
 *
 * @example
 *   const { i18n } = useTranslation();
 *   <span>{pickLocaleValue(sport.name, i18n.language)}</span>
 */
export function pickLocaleValue(
  map: Record<string, string> | undefined,
  preferredLocale: string,
  fallbackLocale: string = 'en-CA',
): string {
  if (!map) return '';
  // Normalise short ('en' / 'fr') to regional ('en-CA' / 'fr-CA') to match
  // the BCP-47 keys the backend produces. Same convention as
  // lib/format.ts's normalizeLocale and the backend's RekPlayLocaleResolver.
  const normalised = preferredLocale.includes('-')
    ? preferredLocale
    : preferredLocale.startsWith('fr')
      ? 'fr-CA'
      : 'en-CA';
  if (map[normalised] !== undefined) return map[normalised];
  if (map[fallbackLocale] !== undefined) return map[fallbackLocale];
  // Last-resort defensive read: take whatever's in the map.
  const firstValue = Object.values(map)[0];
  return firstValue ?? '';
}
